// Sesion deslizante (2026-09-10): helpers puros para renovar el JWT ANTES de
// que expire. Contexto: prod da 8 h a todos; un colocador que entra a las 5 am
// perdia la sesion a la 1 pm en pleno guardado y la foto se perdia. Decision
// de Jose: NO alargar la variable (seguridad). En su lugar, un usuario ACTIVO
// renueva su token cuando le quedan <= RENEW_WINDOW_MIN; uno inactivo muere a
// las 8 h igual que hoy.
//
// Aqui NO se verifica la firma (eso lo hace el backend en /api/auth/refresh);
// solo se lee el claim `exp` para saber cuanto le queda al token.
import { safeLocal, safeSession } from './safeStorage';

// Ventana de renovacion en minutos. Espejo de TOKEN_RENEW_WINDOW_MINUTES del
// backend (default 180): si el front pide antes, el backend responde
// renewed=false y no pasa nada.
export const RENEW_WINDOW_MIN = 180;

// Entre intentos (exitosos o no) esperar al menos 5 min: si el backend dice
// 403 (admin) o falla la red, no martillar en cada request.
const MIN_MS_BETWEEN_ATTEMPTS = 5 * 60 * 1000;

let refreshInFlight = null;
let lastAttemptAt = 0;

// Decodifica el payload del JWT (base64url, sin verificar) y devuelve el claim
// `exp` en SEGUNDOS epoch tal como viene en el token. null si el token esta
// malformado o no trae exp numerico.
export function decodeJwtExp(token) {
  if (typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3 || !parts[1]) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    const exp = payload && payload.exp;
    return (typeof exp === 'number' && Number.isFinite(exp)) ? exp : null;
  } catch {
    return null;
  }
}

// true si al token le quedan MAS de 0 y COMO MAXIMO `ventanaMin` minutos.
// Expirado o sin exp -> false (ya no hay nada que renovar: el backend dara 401).
export function shouldRefresh(token, nowMs = Date.now(), ventanaMin = RENEW_WINDOW_MIN) {
  const exp = decodeJwtExp(token);
  if (exp === null) return false;
  const remainingMs = exp * 1000 - nowMs;
  return remainingMs > 0 && remainingMs <= ventanaMin * 60 * 1000;
}

// El token vive en localStorage ("mantener sesion iniciada") o en
// sessionStorage (solo este tab) — mismo orden de lectura que AuthContext.
// Devuelve tambien el store para reescribirlo en el MISMO lugar.
function readStoredToken() {
  const local = safeLocal.getItem('token');
  if (local) return { token: local, store: safeLocal };
  const session = safeSession.getItem('token');
  if (session) return { token: session, store: safeSession };
  return { token: null, store: null };
}

// Token actual en storage (local primero, luego session) o null. Lo usa el
// interceptor de respuesta de api.js para saber si el token con el que SALIO
// un request ya fue reemplazado por una renovacion mientras el request volaba.
export function getStoredToken() {
  return readStoredToken().token;
}

// Promesa de la renovacion en curso, o null si no hay ninguna. Permite a
// api.js esperar a que aterrice antes de decidir que un 401 es sesion muerta
// (el 401 del request viejo puede llegar al cliente ANTES que la respuesta
// de /refresh que trae el token nuevo).
export function getRefreshInFlight() {
  return refreshInFlight;
}

// Renovacion en segundo plano. `client` es la instancia axios del proyecto
// (se recibe por parametro para que este modulo no importe api.js: evita el
// ciclo api.js <-> session.js y lo deja puro/testeable).
// - Sin token, o fuera de la ventana: no hace nada.
// - Ya hay una renovacion en curso: devuelve esa misma promesa.
// - Ultimo intento hace < 5 min: no hace nada.
// - Si el backend responde renewed=true: guarda el token nuevo donde vivia el
//   viejo y avisa con el evento `pop:token-renewed` (AuthContext lo escucha).
// Los errores se tragan (log en consola): esto NUNCA debe romper un request.
export function maybeRefreshToken(client, nowMs = Date.now()) {
  const { token, store } = readStoredToken();
  if (!token || !shouldRefresh(token, nowMs)) return null;
  if (refreshInFlight) return refreshInFlight;
  if (nowMs - lastAttemptAt < MIN_MS_BETWEEN_ATTEMPTS) return null;
  lastAttemptAt = nowMs;

  refreshInFlight = client.post('/api/auth/refresh', null, { skipAuthRedirect: true })
    .then(res => {
      const data = res && res.data;
      if (!data || !data.renewed || !data.access_token) return false;
      // Si mientras tanto el usuario volvio a entrar (token distinto en el
      // store), no pisar ese token con el renovado del viejo.
      if (store.getItem('token') !== token) return false;
      store.setItem('token', data.access_token);
      window.dispatchEvent(new CustomEvent('pop:token-renewed', { detail: { token: data.access_token } }));
      return true;
    })
    .catch(err => {
      const why = err?.response?.status ? `HTTP ${err.response.status}` : (err?.message || 'red');
      console.warn(`[session] No se pudo renovar el token (${why}); se reintenta en 5 min.`);
      return false;
    })
    .finally(() => { refreshInFlight = null; });
  return refreshInFlight;
}

// Solo para tests: limpia el estado de modulo (en curso + ultimo intento).
export function _resetRefreshStateForTests() {
  refreshInFlight = null;
  lastAttemptAt = 0;
}
